export type EditorSaveStatus =
  | { kind: 'saved' }
  | { kind: 'saving' }
  | { kind: 'error'; message: string };

export type MutationSaveResult = {
  data?: { updateEngagement?: unknown | null } | null;
  errors?: ReadonlyArray<{
    message?: string | null;
    extensions?: Record<string, unknown> | null;
  }> | null;
  error?: unknown;
};

export function humanizeSaveError(message: string | null | undefined): string {
  if (!message) return '';
  const stripped = message
    .replace(/^Couldn't save\. Try again\.\s*/i, '')
    .trim();
  if (!stripped) return '';
  if (
    /^unimplemented\.?$/i.test(stripped) ||
    stripped.toUpperCase() === 'UNIMPLEMENTED'
  ) {
    return "This screen can't be updated on the current server yet.";
  }
  return stripped;
}

export function saveStatusLabel(status: EditorSaveStatus): string {
  if (status.kind === 'saving') return 'Saving';
  if (status.kind === 'saved') return 'Saved';
  const reason = humanizeSaveError(status.message);
  return reason
    ? `Couldn't save. Try again. ${reason}`
    : "Couldn't save. Try again.";
}

function firstMessage(
  errors?: ReadonlyArray<{ message?: string | null }> | null,
) {
  return (
    errors?.map((error) => error?.message).find((message) => message) ?? null
  );
}

export function graphqlErrorMessage(error: unknown): string {
  if (!error || typeof error !== 'object') return 'Save failed';
  const withGraphql = error as {
    graphQLErrors?: Array<{ message?: string }>;
    networkError?: { result?: { errors?: Array<{ message?: string }> } };
    message?: string;
  };
  return (
    firstMessage(withGraphql.graphQLErrors) ||
    firstMessage(withGraphql.networkError?.result?.errors) ||
    withGraphql.message ||
    'Save failed'
  );
}

function nestedValidationMessage(data: MutationSaveResult['data']) {
  const payload = data?.updateEngagement;
  if (!payload || typeof payload !== 'object') return null;
  const withErrors = payload as {
    errors?: Array<{ message?: string | null }> | null;
    validationErrors?: Array<{ message?: string | null }> | null;
  };
  return (
    firstMessage(withErrors.errors) || firstMessage(withErrors.validationErrors)
  );
}

export function mutationErrorMessage(
  result: MutationSaveResult,
): string | null {
  const fromErrors = firstMessage(result.errors);
  if (fromErrors) return fromErrors;

  if (result.error) {
    const fromApollo = graphqlErrorMessage(result.error);
    if (fromApollo) return fromApollo;
  }

  const nested = nestedValidationMessage(result.data);
  if (nested) return nested;

  if (
    result.data &&
    Object.prototype.hasOwnProperty.call(result.data, 'updateEngagement') &&
    result.data.updateEngagement == null
  ) {
    return 'Save failed';
  }

  return null;
}

export function saveStatusFromMutationResult(
  result: MutationSaveResult,
): EditorSaveStatus {
  const message = mutationErrorMessage(result);
  if (message) return { kind: 'error', message };
  return { kind: 'saved' };
}
